import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { Times } from '@styled-icons/fa-solid/Times';
import { Copy } from '@styled-icons/fa-solid/Copy';
import { getAuth } from 'firebase/auth';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { device } from '../../constants/device-sizes';
import { API_URL } from '../../constants/string-constants';
import ModalButton from './ModalButton';
import TitleText from './TitleText';

const CloseButton = styled(Times)`
  cursor: pointer;
  position: absolute;
  width: 2.5rem;
  height: 2.5rem;
  top: 1rem;
  right: 1rem;
  margin: 1rem 1rem;
  font-size: 2rem;
  transition: all 0.2s linear;
  color: var(--clr-sec);
  :hover {
    color: red;
  }
`;

const ModalWrapper = styled.div`
  width: 100%;
  padding: 1rem;
  @media ${device.tablet} {
    width: 50%;
    position: auto;
    top: auto;
  }
  top: 0px;
  position: absolute;
`;

const ModalContent = styled.div`
  padding: 16px;
  background-color: var(--clr-bg);
  width: 100%;
  border-radius: 12px;
  shadow-opacity: 0.18;
  shadow-radius: 4px;
  shadow-color: #000;
  shadow-offset: 0px 4px;
  elevation: 10;
`;

const Description = styled.p`
  color: var(--clr-sec);
  font-size: 16px;
  text-align: center;
  margin: 1rem 0;
`;

const AccountNumberContainer = styled.div`
  background-color: var(--clr-prim);
  border: 2px solid var(--clr-sec);
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  text-align: center;
  position: relative;
`;

const AccountNumberText = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 18px;
  font-weight: 600;
  color: var(--clr-sec);
  letter-spacing: 2px;
  word-break: break-all;
  margin-bottom: 0.5rem;
`;

const CopyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: var(--clr-sec);
  color: var(--clr-bg);
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--clr-tert);
  }
`;

const CopyIcon = styled(Copy)`
  width: 16px;
  height: 16px;
`;

const WarningText = styled.p`
  color: #ff6b6b;
  font-size: 22px;
  text-align: center;
  margin: 1rem 0;
  font-weight: 600;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 1rem 0;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: var(--clr-sec);
  font-size: 14px;
  cursor: pointer;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const ModalButtonSC = styled.div`
  cursor: pointer;
  flex-grow: 1;
  margin: 0 0.5rem;
`;

const AccountNumberModal = ({ visible, onClose, onComplete }) => {
  const [accountNumber, setAccountNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (visible) {
      generateAccountNumber();
    }
  }, [visible]);

  const generateAccountNumber = async () => {
    try {
      setLoading(true);
      setError('');
      const auth = getAuth();

      if (!auth.currentUser) {
        setError('Please sign in to generate account number');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + 'account/generate', {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAccountNumber(data.account_number);
        const analytics = getAnalytics();
        logEvent(analytics, 'account_number_generated');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate account number');
      }
    } catch (error) {
      console.error('Error generating account number:', error);
      setError('Failed to generate account number');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(accountNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      const analytics = getAnalytics();
      logEvent(analytics, 'account_number_copied');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = accountNumber;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleContinue = async () => {
    if (confirmed && accountNumber) {
      try {
        setLoading(true);
        const auth = getAuth();

        if (!auth.currentUser) {
          setError('Please sign in to confirm account number');
          return;
        }

        const token = await auth.currentUser.getIdToken();
        const response = await fetch(API_URL + 'account/confirm', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
          body: JSON.stringify({
            account_number: accountNumber,
          }),
        });

        if (response.ok) {
          const analytics = getAnalytics();
          logEvent(analytics, 'account_number_confirmed');
          onComplete(accountNumber);
        } else {
          const errorData = await response.json();
          setError(errorData.error || 'Failed to confirm account number');
        }
      } catch (error) {
        console.error('Error confirming account number:', error);
        setError('Failed to confirm account number');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal
      isOpen={visible}
      onRequestClose={onClose}
      style={{
        overlay: { zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.5)' },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalContent>
          <CloseButton onClick={onClose} />

          <TitleText>Your Account Number</TitleText>

          <Description>
            Your unique account number has been generated. This is your login
            credential for VentScape Plus.
          </Description>

          {loading ? (
            <Description>Generating account number...</Description>
          ) : error ? (
            <Description style={{ color: '#ff6b6b' }}>{error}</Description>
          ) : (
            <>
              <AccountNumberContainer>
                <AccountNumberText>{accountNumber}</AccountNumberText>
                <CopyContainer>
                  <CopyButton onClick={copyToClipboard}>
                    <CopyIcon />
                    {copied ? 'Copied!' : 'Copy Account Number'}
                  </CopyButton>
                </CopyContainer>
              </AccountNumberContainer>

              <WarningText>
                ⚠️ IMPORTANT: WRITE DOWN THIS ACCOUNT NUMBER NOW! You will not
                get to see it again. Think of it like a password.
              </WarningText>

              <CheckboxContainer>
                <Checkbox
                  type='checkbox'
                  id='confirm-saved'
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                <CheckboxLabel htmlFor='confirm-saved'>
                  I have saved my account number in a safe place
                </CheckboxLabel>
              </CheckboxContainer>

              <ButtonContainer>
                <ModalButtonSC
                  width='45%'
                  as={ModalButton}
                  isAffirmative={false}
                  onClick={onClose}
                >
                  Cancel
                </ModalButtonSC>
                <ModalButtonSC
                  width='45%'
                  as={ModalButton}
                  isAffirmative={true}
                  enabled={confirmed && accountNumber && !loading}
                  isLoading={loading}
                  onClick={handleContinue}
                >
                  {loading ? 'Confirming...' : 'Continue'}
                </ModalButtonSC>
              </ButtonContainer>
            </>
          )}
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};

export default AccountNumberModal;
